import { ChecklistLookupField } from "@/components/checklists/checklist-lookup-field";
import { useTaskChecklistWorkspace, isFloatingChecklistTarget } from "@/components/checklists/task-checklist-workspace";
import { useQuery, useMutation } from "@tanstack/react-query";
import { CategorySelectGroups } from "@/components/category-select-groups";
import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import {
  isEditPermissionError,
  isRevokedEditAccessError,
  REVOKED_EDIT_ACCESS_MESSAGE,
} from "@/lib/permission-errors";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Progress } from "@/components/ui/progress";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import { Label } from "@/components/ui/label";
import {
  CheckSquare, Square, Calendar, Link as LinkIcon, Trash2, Pencil, Plus, Search, ListTodo,
  Target, BookOpen, Milestone as MilestoneIcon, Filter, FileText, List, LayoutGrid, MoreVertical,
  Star, ChevronDown, ChevronRight, GripVertical, Tag, AlertCircle, Clock, Share2, Users, UserPlus,
  File as FileIcon, Download, X, Sparkles, Flag, ExternalLink, Paperclip, MessageSquare, Activity,
  Eye, EyeOff, CalendarPlus, Upload, HardDrive, FolderOpen,
} from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { preventDismissFromSiblingDialog } from "@/lib/dialog-guards";
import { format, isPast, parseISO, endOfDay, startOfDay } from "date-fns";
import { formatDateInput, parseLocalDate } from "@/lib/dateUtils";
import DocumentUploadButton from "@/components/document-upload-button";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors, type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  verticalListSortingStrategy, useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { sanitizeCategory } from "@/lib/categoryUtils";
import { TaskAttachmentsList } from "@/components/tasks/task-attachments";
import MilestoneSubTasks, { type MilestoneSubTaskHandlers } from "@/components/tasks/milestone-subtasks";
import { ScheduleTaskDialog } from "@/components/tasks/subtask-components";
import {
  FriendPicker, AssistantPicker, TagEditor, RecurrenceSection, LinkEntitySelectField, AssignToSelect,
  AssignedToOtherField, resolveTaskAssignment,
} from "./task-form-shared";
import {
  taskFormSchema,
  type TaskFormData, type TaskFormInput, type Goal, type JournalEntry, type Category,
  type Milestone, type FriendWithUser, type AssistantWithUser,
  type PendingSubTask, type PendingTaskMilestone, type Task, type SubTask,
  type KanbanColumn, type TaskProgressEntryWithUser, type Attachment,
} from "./task-types";

const EMPTY_SUBTASKS: SubTask[] = [];

function SortableSubTaskItem({
  subTask,
  rowNumber,
  onToggle,
  onTextChange,
  onTextBlur,
  onDateChange,
  onDelete,
  onStar,
  readOnly,
}: {
  subTask: SubTask;
  rowNumber: number;
  onToggle: (id: string) => void;
  onTextChange: (id: string, text: string) => void;
  onTextBlur: (id: string, text: string) => void;
  onDateChange: (id: string, date: string | null) => void;
  onDelete: (id: string) => void;
  onStar: (id: string) => void;
  readOnly?: boolean;
}) {
  const readOnlyTitle = readOnly ? "View-only shared task — you can't edit sub-tasks" : undefined;
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: subTask.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex flex-col sm:flex-row sm:items-center gap-2 p-2 sm:p-3 border rounded bg-card"
      data-testid={`subtask-item-${subTask.id}`}
    >
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <span className="w-5 text-center text-xs font-mono font-semibold text-muted-foreground flex-shrink-0 select-none">
          {rowNumber}
        </span>

        {readOnly ? (
          <div className="p-1 flex-shrink-0 w-6" aria-hidden="true" />
        ) : (
          <div
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing p-1 flex-shrink-0"
            data-testid={`drag-handle-subtask-${subTask.id}`}
          >
            <GripVertical className="w-4 h-4 text-muted-foreground" />
          </div>
        )}
        
        <input
          type="checkbox"
          checked={subTask.completed}
          disabled={readOnly}
          onChange={() => { if (!readOnly) onToggle(subTask.id); }}
          className="w-4 h-4 rounded border-gray-300 flex-shrink-0"
          title={readOnlyTitle}
          data-testid={`checkbox-subtask-${subTask.id}`}
        />
        
        <Input
          value={subTask.text}
          readOnly={readOnly}
          onChange={(e) => { if (!readOnly) onTextChange(subTask.id, e.target.value); }}
          onBlur={(e) => { if (!readOnly) onTextBlur(subTask.id, e.target.value); }}
          className={`flex-1 min-w-0 ${readOnly ? 'cursor-default ' : ''}${subTask.completed ? 'line-through text-muted-foreground' : ''}`}
          placeholder="Sub-task text"
          title={readOnlyTitle}
          data-testid={`input-subtask-text-${subTask.id}`}
        />
      </div>
      
      <div className="flex items-center gap-2 sm:flex-shrink-0">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={readOnly}
          onClick={() => { if (!readOnly) onStar(subTask.id); }}
          className={`flex-shrink-0 ${readOnly ? "cursor-not-allowed " : ""}${subTask.isStarred ? "text-amber-500 hover:text-amber-600" : "text-muted-foreground/50 hover:text-amber-500"}`}
          title={readOnly ? readOnlyTitle : subTask.isStarred ? "Remove star" : "Star subtask"}
          data-testid={`button-star-subtask-edit-${subTask.id}`}
        >
          <Star className="w-4 h-4" style={subTask.isStarred ? { fill: "#f59e0b" } : {}} />
        </Button>
        <Input
          type="date"
          value={subTask.dueDate ? formatDateInput(new Date(subTask.dueDate)) : ''}
          disabled={readOnly}
          onChange={(e) => { if (!readOnly) onDateChange(subTask.id, e.target.value || null); }}
          className="flex-1 sm:w-40"
          title={readOnlyTitle}
          data-testid={`input-subtask-date-${subTask.id}`}
        />
        
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={readOnly}
          onClick={() => { if (!readOnly) onDelete(subTask.id); }}
          className="text-destructive flex-shrink-0"
          title={readOnlyTitle}
          data-testid={`button-delete-subtask-${subTask.id}`}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

export function EditTaskModal({
  open,
  onOpenChange,
  task,
  goals,
  journalEntries,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  task: Task;
  goals: Goal[];
  journalEntries: JournalEntry[];
}) {
  const [linkType, setLinkType] = useState<"none" | "goal" | "milestone" | "journal">(
    task.goalId ? "goal" : task.milestoneId ? "milestone" : task.journalEntryId ? "journal" : "none"
  );
  const [selectedGoalId, setSelectedGoalId] = useState<string>(task.goalId || "");
  const [schedDialogOpen, setSchedDialogOpen] = useState(false);
  const [currentTag, setCurrentTag] = useState("");
  const [tags, setTags] = useState<string[]>(task.tags || []);
  const initialAssignmentType = (): "Me" | "Friend" | "Assistant" | "Other" => {
    if (task.assignedTo === "Me") return "Me";
    if (task.assignedToUserId) return "Friend"; // corrected to "Assistant" by useEffect once assistants load
    if (task.assignedToOther) return "Other";
    return "Me";
  };
  const [assignmentType, setAssignmentType] = useState<"Me" | "Friend" | "Assistant" | "Other">(initialAssignmentType());
  const [selectedAssistantId, setSelectedAssistantId] = useState<string | null>(null);
  const [localSubTasks, setLocalSubTasks] = useState<SubTask[]>([]);
  const [rowNumberMap, setRowNumberMap] = useState<Map<string, number>>(new Map());
  const [newSubTaskText, setNewSubTaskText] = useState("");
  const [selectedFriendId, setSelectedFriendId] = useState<string | null>(task.assignedToUserId || null);
  const [friendSearchQuery, setFriendSearchQuery] = useState("");
  const taskData = task as any; // Type assertion to access recurrence fields
  const [isRecurring, setIsRecurring] = useState(taskData.isRecurring || false);
  const [recurrenceFrequency, setRecurrenceFrequency] = useState<string>(taskData.recurrenceFrequency || "weekly");
  const [recurrenceEndType, setRecurrenceEndType] = useState<"count" | "date">(
    taskData.recurrenceCount ? "count" : taskData.recurrenceEndDate ? "date" : "count"
  );
  const [recurrenceCount, setRecurrenceCount] = useState<number | undefined>(taskData.recurrenceCount || undefined);
  const [recurrenceEndDate, setRecurrenceEndDate] = useState<string>(
    taskData.recurrenceEndDate ? format(new Date(taskData.recurrenceEndDate), 'yyyy-MM-dd') : ""
  );
  const [selectedOccurrences, setSelectedOccurrences] = useState<Set<string>>(new Set());
  const [bulkActionDialogOpen, setBulkActionDialogOpen] = useState(false);
  const [bulkActionType, setBulkActionType] = useState<'delete' | 'modify' | 'pushout'>('delete');
  const [pushOutDays, setPushOutDays] = useState("");
  const [newBulkDueDate, setNewBulkDueDate] = useState<string>("");
  const [newProgressNote, setNewProgressNote] = useState("");
  const [isProgressSectionOpen, setIsProgressSectionOpen] = useState(true);
  const [hideCompletedInModal, setHideCompletedInModal] = useState(false);

  const { toast } = useToast();
  const { user } = useAuth();

  // Tasks shared with the current user as view-only must not expose editable
  // sub-task controls. The permission is derived reactively from the live
  // /api/shared-tasks cache (falling back to the task prop snapshot) so that
  // invalidating that query after a revoked-access 403 flips an already-open
  // modal to view-only without a manual refresh.
  const { data: liveSharedTasks } = useQuery<Array<{ id: string; permission?: string }>>({
    queryKey: ["/api/shared-tasks"],
    enabled: open && !!(task as any).isShared,
  });
  const liveShare = Array.isArray(liveSharedTasks)
    ? liveSharedTasks.find((s) => s?.id === task.id)
    : undefined;
  const sharedPermission = liveShare
    ? (liveShare.permission === "edit" ? "edit" : "view")
    : (task as any).sharedPermission;
  const subtasksReadOnly = !!(task as any).isShared && sharedPermission !== "edit";

  const { data: categories = [] } = useQuery<Category[]>({
    queryKey: ["/api/categories?includeGoalsOnly=true"],
  });

  const { data: milestones = [] } = useQuery<Milestone[]>({
    queryKey: ["/api/goals", selectedGoalId, "milestones"],
    enabled: linkType === "milestone" && !!selectedGoalId,
  });

  const { data: friends = [] } = useQuery<FriendWithUser[]>({
    queryKey: ["/api/friends"],
    enabled: assignmentType === "Friend",
  });

  const { data: assistants = [] } = useQuery<AssistantWithUser[]>({
    queryKey: ["/api/assistants"],
    enabled: open,
  });

  // Once assistants load, correct the assignmentType if the task is assigned to an assistant
  // (the initial state defaults to "Friend" for any assignedToUserId).
  useEffect(() => {
    if (!open || assistants.length === 0 || !task.assignedToUserId) return;
    const isAssistant = assistants.some(a => a.user.id === task.assignedToUserId);
    if (isAssistant) {
      setAssignmentType("Assistant");
      setSelectedAssistantId(task.assignedToUserId);
      setSelectedFriendId(null);
    }
  }, [open, assistants, task.assignedToUserId]);

  const { data: subTasks = EMPTY_SUBTASKS, isLoading: isLoadingSubTasks } = useQuery<SubTask[]>({
    queryKey: ["/api/tasks", task.id, "subtasks"],
    enabled: open,
  });

  const { data: taskMilestonesData = [], isLoading: isLoadingTaskMilestones } = useQuery<any[]>({
    queryKey: ["/api/tasks", task.id, "task-milestones"],
    enabled: open,
  });

  const [newTaskMilestoneText, setNewTaskMilestoneText] = useState("");
  // Which milestone's "add sub-task" input is currently open (via the + icon).
  const [addSubtaskForMilestone, setAddSubtaskForMilestone] = useState<string | null>(null);

  const createTaskMilestoneMutation = useMutation({
    mutationFn: async (title: string) => {
      const response = await apiRequest("POST", `/api/tasks/${task.id}/task-milestones`, { title });
      return response.json();
    },
    onError: () => {
      toast({ title: "Couldn't add milestone", description: "The milestone was not saved. Please try again.", variant: "destructive" });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/tasks", task.id, "task-milestones"] });
      setNewTaskMilestoneText("");
    },
  });

  const toggleTaskMilestoneMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiRequest("PATCH", `/api/task-milestones/${id}/toggle`, {});
      return response.json();
    },
    onError: () => {
      toast({ title: "Couldn't update milestone", description: "Your change was not saved. Please try again.", variant: "destructive" });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/tasks", task.id, "task-milestones"] });
    },
  });

  const deleteTaskMilestoneMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/task-milestones/${id}`, {});
    },
    onError: () => {
      toast({ title: "Couldn't delete milestone", description: "The milestone was not deleted. Please try again.", variant: "destructive" });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/tasks", task.id, "task-milestones"] });
    },
  });

  // Fetch all recurring task instances if this task is recurring
  const { data: recurringInstances = [] } = useQuery<Task[]>({
    queryKey: ["/api/tasks/recurring-instances", task.id],
    enabled: open && (taskData.isRecurring || taskData.recurrenceParentId !== null),
  });

  // Fetch task progress entries
  const { data: progressEntries = [], isLoading: isLoadingProgress } = useQuery<TaskProgressEntryWithUser[]>({
    queryKey: ["/api/tasks", task.id, "progress"],
    enabled: open,
  });

  // Create progress entry mutation
  const createProgressMutation = useMutation({
    mutationFn: async (notes: string) => {
      const response = await apiRequest("POST", `/api/tasks/${task.id}/progress`, { notes });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/tasks", task.id, "progress"] });
      setNewProgressNote("");
      toast({
        title: "Progress added",
        description: "Progress entry has been added successfully.",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to add progress entry.",
        variant: "destructive",
      });
    },
  });

  // Delete progress entry mutation
  const deleteProgressMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/task-progress/${id}`, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/tasks", task.id, "progress"] });
      toast({
        title: "Progress deleted",
        description: "Progress entry has been deleted.",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to delete progress entry.",
        variant: "destructive",
      });
    },
  });

  const handleAddProgress = () => {
    if (newProgressNote.trim()) {
      createProgressMutation.mutate(newProgressNote.trim());
    }
  };

  useEffect(() => {
    if (subTasks) {
      setLocalSubTasks(subTasks);
      const sorted = [...subTasks].sort((a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );
      const map = new Map<string, number>();
      sorted.forEach((st, i) => map.set(st.id, i + 1));
      setRowNumberMap(map);
    }
  }, [subTasks]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const createSubTaskMutation = useMutation({
    mutationFn: async (input: string | (Partial<SubTask> & { text: string })) => {
      const payload = typeof input === "string" ? { text: input } : input;
      const response = await apiRequest("POST", `/api/tasks/${task.id}/subtasks`, payload);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/tasks", task.id, "subtasks"] });
      queryClient.invalidateQueries({ queryKey: ["/api/subtasks/all"] });
      queryClient.invalidateQueries({ queryKey: ["/api/goals"] });
      setNewSubTaskText("");
    },
    onError: () => {
      toast({ title: "Couldn't add sub-task", description: "The sub-task was not saved. Please try again.", variant: "destructive" });
    },
  });

  const subTaskSubmitLockRef = useRef(false);
  if (!createSubTaskMutation.isPending) subTaskSubmitLockRef.current = false;

  const updateSubTaskMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<SubTask> }) => {
      const response = await apiRequest("PATCH", `/api/subtasks/${id}`, updates);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/tasks", task.id, "subtasks"] });
      queryClient.invalidateQueries({ queryKey: ["/api/subtasks/all"] });
      queryClient.invalidateQueries({ queryKey: ["/api/goals"] });
    },
    onError: () => {
      toast({ title: "Couldn't save sub-task", description: "Your change was not saved. Please try again.", variant: "destructive" });
    },
  });

  const deleteSubTaskMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/subtasks/${id}`, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/tasks", task.id, "subtasks"] });
      queryClient.invalidateQueries({ queryKey: ["/api/subtasks/all"] });
      queryClient.invalidateQueries({ queryKey: ["/api/goals"] });
    },
    onError: () => {
      // Restore the sub-task in the UI by re-fetching from the server so an
      // optimistic or stale removal (e.g. from a 403 on a view-only share)
      // doesn't cause the item to silently vanish.
      queryClient.invalidateQueries({ queryKey: ["/api/tasks", task.id, "subtasks"] });
      queryClient.invalidateQueries({ queryKey: ["/api/subtasks/all"] });
      toast({ title: "Couldn't delete sub-task", description: "The sub-task was not deleted. Please try again.", variant: "destructive" });
    },
  });

  const reorderSubTasksMutation = useMutation({
    mutationFn: async (updates: Array<{ id: string; displayOrder: number }>) => {
      await apiRequest("PATCH", "/api/subtasks/reorder", { updates });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/tasks", task.id, "subtasks"] });
      queryClient.invalidateQueries({ queryKey: ["/api/subtasks/all"] });
      queryClient.invalidateQueries({ queryKey: ["/api/goals"] });
    },
    onError: (error: any) => {
      // A 403 carrying skippedSubTaskIds means the share was downgraded from
      // edit to view mid-session — tell the user their access changed instead
      // of showing the generic failure copy.
      if (isRevokedEditAccessError(error)) {
        toast({
          title: "Edit access removed",
          description: REVOKED_EDIT_ACCESS_MESSAGE,
          variant: "destructive",
        });
        // Refetch the share/permission data so the UI flips to view-only
        // (hides drag handles / edit controls) without a manual refresh.
        queryClient.invalidateQueries({ queryKey: ["/api/shared-tasks"] });
      } else {
        const permissionError = isEditPermissionError(error);
        toast({
          title: permissionError ? "View Only" : "Error",
          description: permissionError
            ? "You can't reorder sub-tasks on this shared task — you only have view access."
            : "Failed to reorder the sub-tasks. Please try again.",
          variant: "destructive",
        });
      }
      // Snap the list back to the server order
      queryClient.invalidateQueries({ queryKey: ["/api/tasks", task.id, "subtasks"] });
      queryClient.invalidateQueries({ queryKey: ["/api/subtasks/all"] });
    },
  });

  const handleSubTaskToggle = (id: string) => {
    const subTask = localSubTasks.find(st => st.id === id);
    if (subTask) {
      updateSubTaskMutation.mutate({ id, updates: { completed: !subTask.completed } });
    }
  };

  const handleSubTaskStar = (id: string) => {
    const subTask = localSubTasks.find(st => st.id === id);
    if (subTask) {
      const previous = !!subTask.isStarred;
      setLocalSubTasks(prev => prev.map(st => st.id === id ? { ...st, isStarred: !previous } : st));
      updateSubTaskMutation.mutate(
        { id, updates: { isStarred: !previous } },
        {
          onError: (error) => {
            // Roll back the optimistic toggle so the UI matches the server state.
            setLocalSubTasks(prev => prev.map(st => st.id === id ? { ...st, isStarred: previous } : st));
            const permissionError = isEditPermissionError(error);
            toast({
              title: permissionError ? "View Only" : "Couldn't update star",
              description: permissionError
                ? "You can't edit this shared task — you only have view access."
                : "The change wasn't saved. Please try again.",
              variant: "destructive",
            });
          },
        },
      );
    }
  };

  const handleSubTaskTextChange = (id: string, text: string) => {
    setLocalSubTasks(prev => prev.map(st => st.id === id ? { ...st, text } : st));
  };

  const handleSubTaskTextBlur = (id: string, text: string) => {
    updateSubTaskMutation.mutate({ id, updates: { text } });
  };

  const handleSubTaskDateChange = (id: string, date: string | null) => {
    // Convert date string to proper ISO format to avoid timezone issues
    const isoDate = date ? parseLocalDate(date).toISOString() : null;
    updateSubTaskMutation.mutate({ id, updates: { dueDate: isoDate } });
  };

  const handleSubTaskDelete = (id: string) => {
    deleteSubTaskMutation.mutate(id);
  };

  const handleAddSubTask = () => {
    if (subTaskSubmitLockRef.current || createSubTaskMutation.isPending) return;
    if (newSubTaskText.trim()) {
      subTaskSubmitLockRef.current = true;
      createSubTaskMutation.mutate(newSubTaskText.trim());
    }
  };

  // Handlers for milestone-scoped sub-tasks (and their nested sub-sub-tasks).
  const milestoneSubTaskHandlers: MilestoneSubTaskHandlers = {
    onCreate: (fields) => {
      if (subTaskSubmitLockRef.current || createSubTaskMutation.isPending) return;
      subTaskSubmitLockRef.current = true;
      createSubTaskMutation.mutate({
        ...fields,
        // Convert a "YYYY-MM-DD" date to a local-midnight ISO instant to avoid
        // west-of-UTC off-by-one day shifts.
        dueDate: fields.dueDate ? parseLocalDate(fields.dueDate).toISOString() : null,
      });
    },
    onUpdate: (id, updates) => updateSubTaskMutation.mutate({ id, updates }),
    onToggle: handleSubTaskToggle,
    onStar: handleSubTaskStar,
    onDelete: handleSubTaskDelete,
    onDateChange: handleSubTaskDateChange,
  };

  // General (non-milestone, top-level) sub-tasks shown in the Sub Tasks
  // section; milestone-scoped and nested sub-tasks render under milestones.
  const generalSubTasks = localSubTasks.filter(st => !st.milestoneId && !st.parentSubTaskId);

  const handleSubTaskDragEnd = (event: DragEndEvent) => {
    if (subtasksReadOnly) return;
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = generalSubTasks.findIndex(st => st.id === active.id);
      const newIndex = generalSubTasks.findIndex(st => st.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newOrder = arrayMove(generalSubTasks, oldIndex, newIndex);
        setLocalSubTasks(prev => [...newOrder, ...prev.filter(st => st.milestoneId || st.parentSubTaskId)]);

        const updates = newOrder.map((st, index) => ({
          id: st.id,
          displayOrder: index,
        }));
        reorderSubTasksMutation.mutate(updates);
      }
    }
  };

  // Sort recurring instances by due date
  const sortedRecurringInstances = useMemo(() => {
    if (!recurringInstances || recurringInstances.length === 0) return [];
    
    return [...recurringInstances].sort((a, b) => {
      if (!a.dueDate) return 1;
      if (!b.dueDate) return -1;
      return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
    });
  }, [recurringInstances]);

  const toggleOccurrence = (taskId: string) => {
    setSelectedOccurrences(prev => {
      const newSet = new Set(prev);
      if (newSet.has(taskId)) {
        newSet.delete(taskId);
      } else {
        newSet.add(taskId);
      }
      return newSet;
    });
  };

  const toggleAllOccurrences = () => {
    if (selectedOccurrences.size === sortedRecurringInstances.length) {
      setSelectedOccurrences(new Set());
    } else {
      setSelectedOccurrences(new Set(sortedRecurringInstances.map(t => t.id)));
    }
  };

  const handleBulkAction = (actionType: 'delete' | 'modify' | 'pushout') => {
    if (selectedOccurrences.size === 0) {
      toast({
        title: "No occurrences selected",
        description: "Please select at least one occurrence to perform bulk actions.",
        variant: "destructive",
      });
      return;
    }
    setBulkActionType(actionType);
    setBulkActionDialogOpen(true);
  };

  const executeBulkAction = async () => {
    // For now, we'll just show a toast. Backend implementation will come next
    const selectedIndices = Array.from(selectedOccurrences);
    
    if (bulkActionType === 'delete') {
      toast({
        title: "Delete occurrences",
        description: `Deleting ${selectedIndices.length} occurrence(s)...`,
      });
    } else if (bulkActionType === 'modify') {
      if (!newBulkDueDate) {
        toast({
          title: "Invalid date",
          description: "Please select a new due date.",
          variant: "destructive",
        });
        return;
      }
      toast({
        title: "Modify occurrences",
        description: `Updating due dates for ${selectedIndices.length} occurrence(s)...`,
      });
    } else if (bulkActionType === 'pushout') {
      const days = parseInt(pushOutDays);
      if (!pushOutDays || isNaN(days) || days < 1) {
        toast({
          title: "Invalid days",
          description: "Please enter a valid number of days (minimum 1).",
          variant: "destructive",
        });
        return;
      }
      toast({
        title: "Push out occurrences",
        description: `Pushing out ${selectedIndices.length} occurrence(s) by ${days} days...`,
      });
    }
    
    setBulkActionDialogOpen(false);
    setSelectedOccurrences(new Set());
  };

  const form = useForm<TaskFormInput, any, TaskFormData>({
    resolver: zodResolver(taskFormSchema),
    defaultValues: {
      title: task.title,
      description: task.description || undefined,
      completed: task.completed,
      category: sanitizeCategory(task.category) || "Uncategorized",
      priority: (task.priority as "low" | "medium" | "high" | "urgent") || "medium",
      tags: task.tags || [],
      assignedTo: task.assignedTo || "Me",
      assignedToOther: task.assignedToOther || undefined,
      dueDate: task.dueDate ? formatDateInput(new Date(task.dueDate)) : undefined,
      linkType: linkType,
      goalId: task.goalId || undefined,
      milestoneId: task.milestoneId || undefined,
      journalEntryId: task.journalEntryId || undefined,
      checklistId: task.checklistId ?? null,
    },
  });

  useEffect(() => {
    if (open) form.setValue("checklistId", task.checklistId ?? null);
  }, [open, task.id]);

  const updateTaskMutation = useMutation({
    mutationFn: async (data: TaskFormData) => {
      // Determine assignedTo and assignedToUserId based on assignment type
      // (shared guard: Friend with no selection / Other with a blank name
      // safely fall back to "Me").
      const {
        assignedTo: assignedToValue,
        assignedToOther: assignedToOtherValue,
        assignedToUserId: assignedToUserIdValue,
      } = resolveTaskAssignment({
        assignmentType,
        selectedFriendId,
        friends,
        selectedAssistantId,
        assistants,
        assignedToOther: data.assignedToOther,
      });

      const taskData: any = {
        title: data.title,
        description: data.description || null,
        completed: data.completed || false,
        category: data.category || "Uncategorized",
        priority: data.priority || "medium",
        tags: tags,
        assignedTo: assignedToValue,
        assignedToOther: assignedToOtherValue,
        assignedToUserId: assignedToUserIdValue,
        dueDate: data.dueDate ? parseLocalDate(data.dueDate).toISOString() : null,
        goalId: null,
        milestoneId: null,
        journalEntryId: null,
        checklistId: data.checklistId ?? null,
      };

      if (linkType === "goal" && data.goalId) {
        taskData.goalId = data.goalId;
      } else if (linkType === "milestone" && data.milestoneId) {
        taskData.milestoneId = data.milestoneId;
      } else if (linkType === "journal" && data.journalEntryId) {
        taskData.journalEntryId = data.journalEntryId;
      }

      // Add recurrence data if enabled
      if (isRecurring) {
        taskData.isRecurring = true;
        taskData.recurrenceFrequency = recurrenceFrequency;
        if (recurrenceEndType === "count") {
          taskData.recurrenceCount = recurrenceCount;
          taskData.recurrenceEndDate = null;
        } else {
          taskData.recurrenceCount = null;
          taskData.recurrenceEndDate = recurrenceEndDate ? parseLocalDate(recurrenceEndDate).toISOString() : null;
        }
      } else {
        taskData.isRecurring = false;
        taskData.recurrenceFrequency = null;
        taskData.recurrenceCount = null;
        taskData.recurrenceEndDate = null;
      }

      const response = await apiRequest("PATCH", `/api/tasks/${task.id}`, taskData);
      return response.json();
    },
    onSuccess: async () => {
      // Auto-share the task if a friend was selected
      if (assignmentType === "Friend" && selectedFriendId) {
        try {
          const shareResponse = await apiRequest("POST", "/api/shares/tasks", {
            taskId: task.id,
            sharedWithId: selectedFriendId,
            permission: "edit"
          });
          if (shareResponse.ok) {
            toast({
              title: "Task shared",
              description: `Task automatically shared with the assigned friend.`,
            });
          }
        } catch (error) {
          console.error('Failed to auto-share task:', error);
        }
      }
      
      queryClient.invalidateQueries({ queryKey: ["/api/tasks"] });
      queryClient.invalidateQueries({ queryKey: ["/api/tasks/assigned-to-me"] });
      queryClient.invalidateQueries({ queryKey: ["/api/shares/tasks/sent"] });
      onOpenChange(false);
    },
    onError: (error: Error) => {
      // Surface server-side rejections (e.g. no edit permission on a shared
      // task) so the user knows why their change was not saved.
      const permissionError = isEditPermissionError(error);
      toast({
        title: permissionError ? "View Only" : "Couldn't update task",
        description: permissionError
          ? "You can't edit this shared task — you only have view access."
          : error?.message || "Your change was not saved.",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: TaskFormData) => {
    updateTaskMutation.mutate(data);
  };

  const { isChecklistOpen } = useTaskChecklistWorkspace();

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal={!isChecklistOpen}>
      <DialogContent
        className="w-screen h-screen m-0 rounded-none overflow-y-auto"
        data-testid="dialog-edit-task"
        onInteractOutside={(e) => {
          if (isFloatingChecklistTarget(e.target) || isChecklistOpen) { e.preventDefault(); return; }
          preventDismissFromSiblingDialog(e);
        }}
      >
        <DialogHeader>
          <DialogTitle>Edit Task</DialogTitle>
          <DialogDescription>Update task details and links.</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Title *</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Task title" data-testid="input-edit-task-title" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="checklistId"
              render={({ field }) => (
                <FormItem>
                  <ChecklistLookupField value={field.value ?? null} onChange={(id) => field.onChange(id)} enabled={open} />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea {...field} value={field.value || ""} placeholder="Task description (optional)" rows={3} data-testid="input-edit-task-description" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Task Milestones Section */}
            <div className="space-y-3">
              <FormLabel>Milestones</FormLabel>
              {isLoadingTaskMilestones ? (
                <p className="text-sm text-muted-foreground">Loading milestones...</p>
              ) : (
                <div className="space-y-2">
                  {taskMilestonesData.map((m: any) => (
                    <div key={m.id} className="space-y-1.5">
                    <div className="flex items-center gap-2 rounded-md border bg-muted/30 px-3 py-2">
                      <button
                        type="button"
                        onClick={() => toggleTaskMilestoneMutation.mutate(m.id)}
                        className="shrink-0"
                      >
                        {m.completed ? (
                          <CheckSquare className="h-4 w-4 text-green-500" />
                        ) : (
                          <Flag className="h-4 w-4 text-muted-foreground" />
                        )}
                      </button>
                      <span className={`flex-1 text-sm ${m.completed ? "line-through text-muted-foreground" : ""}`}>
                        {m.title}
                      </span>
                      {m.dueDate && (
                        <span className="text-xs text-muted-foreground">{format(new Date(m.dueDate), "MMM d")}</span>
                      )}
                      {!subtasksReadOnly && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-muted-foreground hover:text-primary"
                          title="Add sub-task to this milestone"
                          onClick={() => setAddSubtaskForMilestone(prev => prev === m.id ? null : m.id)}
                          data-testid={`button-add-subtask-milestone-modal-${m.id}`}
                        >
                          <Plus className="h-3 w-3" />
                        </Button>
                      )}
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                        onClick={() => deleteTaskMilestoneMutation.mutate(m.id)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                    <MilestoneSubTasks
                      milestoneId={m.id}
                      allSubTasks={localSubTasks}
                      handlers={milestoneSubTaskHandlers}
                      readOnly={subtasksReadOnly}
                      showAddInput={addSubtaskForMilestone === m.id}
                      isCreating={createSubTaskMutation.isPending}
                    />
                    </div>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <Input
                  value={newTaskMilestoneText}
                  onChange={(e) => setNewTaskMilestoneText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (newTaskMilestoneText.trim()) createTaskMilestoneMutation.mutate(newTaskMilestoneText.trim());
                    }
                  }}
                  placeholder="Add milestone..."
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { if (newTaskMilestoneText.trim()) createTaskMilestoneMutation.mutate(newTaskMilestoneText.trim()); }}
                  disabled={!newTaskMilestoneText.trim() || createTaskMilestoneMutation.isPending}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add
                </Button>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <FormLabel>Sub Tasks</FormLabel>
                {localSubTasks.some(st => st.completed) && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
                    onClick={() => setHideCompletedInModal(h => !h)}
                  >
                    {hideCompletedInModal ? (
                      <><Eye className="h-3 w-3 mr-1" />{localSubTasks.filter(st => st.completed).length} hidden</>
                    ) : (
                      <><EyeOff className="h-3 w-3 mr-1" />Hide done</>
                    )}
                  </Button>
                )}
              </div>
              {isLoadingSubTasks ? (
                <p className="text-sm text-muted-foreground">Loading sub-tasks...</p>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleSubTaskDragEnd}
                >
                  <SortableContext
                    items={generalSubTasks.map(st => st.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-2" data-testid="subtasks-list">
                      {generalSubTasks.filter(st => hideCompletedInModal ? !st.completed : true).map((subTask) => (
                        <SortableSubTaskItem
                          key={subTask.id}
                          subTask={subTask}
                          rowNumber={rowNumberMap.get(subTask.id) ?? localSubTasks.indexOf(subTask) + 1}
                          onToggle={handleSubTaskToggle}
                          onTextChange={handleSubTaskTextChange}
                          onTextBlur={handleSubTaskTextBlur}
                          onDateChange={handleSubTaskDateChange}
                          onDelete={handleSubTaskDelete}
                          onStar={handleSubTaskStar}
                          readOnly={subtasksReadOnly}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )}
              {subtasksReadOnly ? (
                <p className="text-xs text-muted-foreground" data-testid="text-subtasks-view-only">
                  View-only shared task — you can't edit sub-tasks
                </p>
              ) : (
                <div className="flex gap-2">
                  <Input
                    value={newSubTaskText}
                    onChange={(e) => setNewSubTaskText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddSubTask();
                      }
                    }}
                    placeholder="Add new sub-task..."
                    data-testid="input-new-subtask"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleAddSubTask}
                    disabled={!newSubTaskText.trim() || createSubTaskMutation.isPending}
                    data-testid="button-add-subtask"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add
                  </Button>
                </div>
              )}
            </div>

            <Collapsible open={isProgressSectionOpen} onOpenChange={setIsProgressSectionOpen}>
              <div className="space-y-3">
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" className="w-full flex items-center justify-between p-0 h-auto hover:bg-transparent">
                    <div className="flex items-center gap-2">
                      <MessageSquare className="h-4 w-4" />
                      <FormLabel className="cursor-pointer">Progress Tracking</FormLabel>
                      <Badge variant="secondary" className="text-xs">
                        {progressEntries.length}
                      </Badge>
                    </div>
                    {isProgressSectionOpen ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="space-y-3">
                  {isLoadingProgress ? (
                    <p className="text-sm text-muted-foreground">Loading progress entries...</p>
                  ) : progressEntries.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No progress entries yet. Add one below.</p>
                  ) : (
                    <div className="space-y-2" data-testid="progress-entries-list">
                      {progressEntries.map((entry) => {
                        const displayName = entry.user.firstName && entry.user.lastName
                          ? `${entry.user.firstName} ${entry.user.lastName}`
                          : entry.user.email || "Unknown User";
                        const canDelete = user?.id === entry.userId;
                        
                        return (
                          <Card key={entry.id} className="p-3">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                                  <span className="font-medium">{displayName}</span>
                                  <span>•</span>
                                  <span>{format(new Date(entry.createdAt), "MMM d, yyyy 'at' h:mm a")}</span>
                                </div>
                                <p className="text-sm">{entry.notes}</p>
                              </div>
                              {canDelete && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => deleteProgressMutation.mutate(entry.id)}
                                  disabled={deleteProgressMutation.isPending}
                                  className="text-destructive flex-shrink-0"
                                  data-testid={`button-delete-progress-${entry.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </Card>
                        );
                      })}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Input
                      value={newProgressNote}
                      onChange={(e) => setNewProgressNote(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddProgress();
                        }
                      }}
                      placeholder="Add progress note..."
                      data-testid="input-new-progress"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleAddProgress}
                      disabled={!newProgressNote.trim() || createProgressMutation.isPending}
                      data-testid="button-add-progress"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Progress
                    </Button>
                  </div>
                </CollapsibleContent>
              </div>
            </Collapsible>

            <FormField
              control={form.control}
              name="category"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Category</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value || "Uncategorized"}>
                    <FormControl>
                      <SelectTrigger data-testid="select-edit-task-category">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="Uncategorized">Uncategorized</SelectItem>
                      <CategorySelectGroups categories={categories.filter(cat => cat.name !== "Uncategorized")} />
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="priority"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Priority</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value || "medium"}>
                    <FormControl>
                      <SelectTrigger data-testid="select-edit-task-priority">
                        <SelectValue placeholder="Select priority" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="dueDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Due Date {isRecurring && <span className="text-destructive">* (Required for recurring tasks)</span>}</FormLabel>
                  <FormControl>
                    <Input {...field} type="date" data-testid="input-edit-task-due-date" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Recurring Task Section */}
            <RecurrenceSection
              isRecurring={isRecurring}
              onToggleRecurring={() => setIsRecurring(!isRecurring)}
              recurrenceFrequency={recurrenceFrequency}
              onRecurrenceFrequencyChange={setRecurrenceFrequency}
              recurrenceEndType={recurrenceEndType}
              onRecurrenceEndTypeChange={setRecurrenceEndType}
              recurrenceCount={recurrenceCount}
              onRecurrenceCountChange={setRecurrenceCount}
              recurrenceEndDate={recurrenceEndDate}
              onRecurrenceEndDateChange={setRecurrenceEndDate}
              frequencyOptions={[
                { value: "daily", label: "Daily" },
                { value: "weekly", label: "Weekly" },
                { value: "monthly", label: "Monthly" },
                { value: "yearly", label: "Yearly" },
              ]}
              idSuffix="-edit"
            >
              {/* Occurrences List */}
              {sortedRecurringInstances.length > 0 && (
                    <div className="space-y-3 mt-4">
                      <div className="flex items-center justify-between">
                        <FormLabel className="text-base">Task Occurrences ({sortedRecurringInstances.length})</FormLabel>
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={toggleAllOccurrences}
                            data-testid="button-select-all-occurrences"
                          >
                            {selectedOccurrences.size === sortedRecurringInstances.length ? "Deselect All" : "Select All"}
                          </Button>
                        </div>
                      </div>

                      {selectedOccurrences.size > 0 && (
                        <div className="flex gap-2 flex-wrap">
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            onClick={() => handleBulkAction('delete')}
                            data-testid="button-bulk-delete"
                          >
                            <Trash2 className="h-3 w-3 mr-1" />
                            Delete ({selectedOccurrences.size})
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleBulkAction('modify')}
                            data-testid="button-bulk-modify"
                          >
                            <Calendar className="h-3 w-3 mr-1" />
                            Change Date ({selectedOccurrences.size})
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleBulkAction('pushout')}
                            data-testid="button-bulk-pushout"
                          >
                            <Clock className="h-3 w-3 mr-1" />
                            Push Out ({selectedOccurrences.size})
                          </Button>
                        </div>
                      )}

                      <div className="max-h-64 overflow-y-auto border rounded-lg">
                        <div className="divide-y">
                          {sortedRecurringInstances.map((instance, index) => (
                            <div
                              key={instance.id}
                              className="flex items-center gap-3 p-3 hover:bg-muted/50 transition-colors"
                              data-testid={`occurrence-${instance.id}`}
                            >
                              <Checkbox
                                checked={selectedOccurrences.has(instance.id)}
                                onCheckedChange={() => toggleOccurrence(instance.id)}
                                data-testid={`checkbox-occurrence-${instance.id}`}
                              />
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <Calendar className="h-4 w-4 text-muted-foreground" />
                                  <span className="font-medium">
                                    Occurrence #{index + 1}
                                  </span>
                                  {instance.completed && (
                                    <Badge variant="default" className="text-xs">Completed</Badge>
                                  )}
                                </div>
                                {instance.dueDate && (
                                  <p className="text-sm text-muted-foreground mt-1">
                                    {format(new Date(instance.dueDate), "EEEE, MMMM d, yyyy")}
                                  </p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
            </RecurrenceSection>

            <TagEditor
              tags={tags}
              onTagsChange={setTags}
              currentTag={currentTag}
              onCurrentTagChange={setCurrentTag}
              inputTestId="input-edit-task-tag"
              addButtonTestId="button-edit-add-tag"
              removeTagTestIdPrefix="button-edit-remove-tag"
            />

            <AssignToSelect
              assignmentType={assignmentType}
              onAssignmentTypeChange={setAssignmentType}
              onSelectedFriendIdChange={setSelectedFriendId}
              onFriendSearchQueryChange={setFriendSearchQuery}
              onSelectedAssistantIdChange={setSelectedAssistantId}
              hasAssistants={assistants.length > 0}
              form={form}
              testId="select-edit-assigned-to"
            />

            {assignmentType === "Friend" && (
              <FriendPicker
                friends={friends}
                friendSearchQuery={friendSearchQuery}
                onFriendSearchQueryChange={setFriendSearchQuery}
                selectedFriendId={selectedFriendId}
                onSelectFriend={setSelectedFriendId}
                friendTestIdPrefix="select-edit-friend"
                selectedHint="This task will be automatically shared with the selected friend when saved."
              />
            )}

            {assignmentType === "Assistant" && (
              <AssistantPicker
                assistants={assistants}
                selectedAssistantId={selectedAssistantId}
                onSelectAssistant={setSelectedAssistantId}
                testIdPrefix="select-edit-assistant"
              />
            )}

            {assignmentType === "Other" && (
              <AssignedToOtherField control={form.control} testId="input-edit-assigned-to-other" />
            )}

            <div className="space-y-3">
              <FormLabel>Link Type</FormLabel>
              <RadioGroup value={linkType} onValueChange={(v) => setLinkType(v as typeof linkType)} data-testid="radio-edit-link-type">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="none" id="edit-none" data-testid="radio-edit-link-none" />
                  <label htmlFor="edit-none" className="cursor-pointer">None (Standalone Task)</label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="goal" id="edit-goal" data-testid="radio-edit-link-goal" />
                  <label htmlFor="edit-goal" className="cursor-pointer">Link to Goal</label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="milestone" id="edit-milestone" data-testid="radio-edit-link-milestone" />
                  <label htmlFor="edit-milestone" className="cursor-pointer">Link to Milestone</label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="journal" id="edit-journal" data-testid="radio-edit-link-journal" />
                  <label htmlFor="edit-journal" className="cursor-pointer">Link to Journal Entry</label>
                </div>
              </RadioGroup>
            </div>

            {linkType === "goal" && (
              <LinkEntitySelectField
                control={form.control}
                name="goalId"
                label="Select Goal"
                placeholder="Select a goal"
                testId="select-edit-goal"
                options={goals.map((goal) => ({ id: goal.id, title: goal.title }))}
              />
            )}

            {linkType === "milestone" && (
              <>
                <div>
                  <FormLabel>Select Goal First</FormLabel>
                  <Select onValueChange={setSelectedGoalId} value={selectedGoalId}>
                    <SelectTrigger data-testid="select-edit-goal-for-milestone">
                      <SelectValue placeholder="Select a goal" />
                    </SelectTrigger>
                    <SelectContent>
                      {goals.map((goal) => (
                        <SelectItem key={goal.id} value={goal.id}>
                          {goal.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {selectedGoalId && (
                  <LinkEntitySelectField
                    control={form.control}
                    name="milestoneId"
                    label="Select Milestone"
                    placeholder="Select a milestone"
                    testId="select-edit-milestone"
                    options={milestones.map((milestone) => ({ id: milestone.id, title: milestone.title }))}
                  />
                )}
              </>
            )}

            {linkType === "journal" && (
              <LinkEntitySelectField
                control={form.control}
                name="journalEntryId"
                label="Select Journal Entry"
                placeholder="Select a journal entry"
                testId="select-edit-journal-entry"
                options={journalEntries.map((entry) => ({ id: entry.id, title: entry.title }))}
              />
            )}

            {/* Document Attachments */}
            <div className="space-y-2 pt-2 border-t">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold text-sm text-muted-foreground">Documents</h4>
                <DocumentUploadButton
                  entityType="task"
                  entityId={task.id}
                />
              </div>
              <TaskAttachmentsList taskId={task.id} />
            </div>

            <DialogFooter className="flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                className="text-indigo-600 border-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-950 mr-auto"
                onClick={() => setSchedDialogOpen(true)}
              >
                <CalendarPlus className="h-4 w-4 mr-2" />
                Add to Calendar
              </Button>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="button-cancel-edit">
                Cancel
              </Button>
              <Button type="submit" disabled={updateTaskMutation.isPending} data-testid="button-submit-edit-task">
                {updateTaskMutation.isPending ? "Updating..." : "Update Task"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>

      <ScheduleTaskDialog task={task} open={schedDialogOpen} onOpenChange={setSchedDialogOpen} />

      {/* Bulk Action Dialog */}
      <Dialog open={bulkActionDialogOpen} onOpenChange={setBulkActionDialogOpen}>
        <DialogContent data-testid="dialog-bulk-action">
          <DialogHeader>
            <DialogTitle>
              {bulkActionType === 'delete' && 'Delete Selected Occurrences'}
              {bulkActionType === 'modify' && 'Modify Due Dates'}
              {bulkActionType === 'pushout' && 'Push Out Occurrences'}
            </DialogTitle>
            <DialogDescription>
              {bulkActionType === 'delete' && `You are about to delete ${selectedOccurrences.size} occurrence(s). This action cannot be undone.`}
              {bulkActionType === 'modify' && `Set a new due date for ${selectedOccurrences.size} selected occurrence(s).`}
              {bulkActionType === 'pushout' && `Specify how many days to push out ${selectedOccurrences.size} occurrence(s).`}
            </DialogDescription>
          </DialogHeader>

          {bulkActionType === 'modify' && (
            <div className="space-y-2">
              <label className="text-sm font-medium">New Due Date</label>
              <Input
                type="date"
                value={newBulkDueDate}
                onChange={(e) => setNewBulkDueDate(e.target.value)}
                data-testid="input-bulk-due-date"
              />
            </div>
          )}

          {bulkActionType === 'pushout' && (
            <div className="space-y-2">
              <label className="text-sm font-medium">Push Out By (Days)</label>
              <Input
                type="number"
                min="1"
                value={pushOutDays}
                onChange={(e) => setPushOutDays(e.target.value)}
                placeholder="Enter number of days"
                data-testid="input-push-out-days"
              />
              <p className="text-xs text-muted-foreground">
                {pushOutDays ? `Each selected occurrence will be delayed by ${pushOutDays} day(s)` : 'Enter the number of days to delay each occurrence'}
              </p>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setBulkActionDialogOpen(false)}
              data-testid="button-cancel-bulk-action"
            >
              Cancel
            </Button>
            <Button
              variant={bulkActionType === 'delete' ? 'destructive' : 'default'}
              onClick={executeBulkAction}
              data-testid="button-confirm-bulk-action"
            >
              {bulkActionType === 'delete' && 'Delete'}
              {bulkActionType === 'modify' && 'Update Dates'}
              {bulkActionType === 'pushout' && 'Push Out'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
